'use client'

import { Check, ChevronsUpDown } from 'lucide-react'
import * as React from 'react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/utils/cn'

export interface FilterableComboboxItem {
  value: string
  label: string
  description?: string
  meta?: string
}

export interface FilterableComboboxProps {
  value: string
  onValueChange: (value: string) => void
  items: FilterableComboboxItem[]
  placeholder?: string
  searchPlaceholder?: string
  disabled?: boolean
  className?: string
  emptyMessage?: string
}

export function FilterableCombobox({
  value,
  onValueChange,
  items,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  disabled = false,
  className,
  emptyMessage = 'No results found.',
}: FilterableComboboxProps) {
  const [open, setOpen] = React.useState(false)

  const selectedItem = items.find(item => item.value === value)

  // Create a lookup map to handle cmdk's lowercase label behavior
  const labelToValue = React.useMemo(() => {
    const map = new Map<string, string>()
    items.forEach(item => {
      // cmdk lowercases the value for comparison
      map.set(item.label.toLowerCase(), item.value)
    })
    return map
  }, [items])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between h-8 text-xs font-normal',
            !value && 'text-muted-foreground',
            className
          )}
        >
          <span className="truncate">{selectedItem ? selectedItem.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[calc(100vw-2rem)] p-0 md:w-[400px]" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {items.map(item => (
                <CommandItem
                  key={item.value}
                  value={item.label}
                  keywords={[item.label, item.description || '', item.meta || '']}
                  onSelect={selectedLabel => {
                    // cmdk passes the label (lowercased), we need to find the actual value
                    const actualValue = labelToValue.get(selectedLabel.toLowerCase())
                    if (actualValue) {
                      onValueChange(actualValue === value ? '' : actualValue)
                      setOpen(false)
                    }
                  }}
                >
                  <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                    <span className="font-medium truncate">{item.label}</span>
                    {item.description && (
                      <span className="text-xs text-muted-foreground truncate">
                        {item.description}
                      </span>
                    )}
                    {item.meta && (
                      <span className="text-xs text-muted-foreground truncate">{item.meta}</span>
                    )}
                  </div>
                  <Check
                    className={cn(
                      'ml-2 h-4 w-4 shrink-0',
                      value === item.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
